import { collection, doc, getDocs, orderBy, query, Timestamp, where } from 'firebase/firestore'
import { db } from '../firebase/firebase'
import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'
import { createFetchThunk } from '../store/fetch'
import { dateFormatUS, setEndDate, setStartDate } from '../dateFormat'

const q = query(collection(db, 'audits'), orderBy('deadline', 'asc'))
const access = ['superadmin', 'admin', 'operator', 'developer']
const today = new Date()
const beginMonth = new Date(today.getFullYear(), today.getMonth(), 1)

export const fetchAudits = createFetchThunk('audits', q, access)
export const fetchAuditsForUser = createAsyncThunk('auditsByUser/fetch', async (_, thunkAPI) => {
  const userData = thunkAPI.getState().user.userData
  if (!userData?.uid) return []
  const auditorRef = doc(db, 'users', userData.uid)
  const userQuery = query(collection(db, 'audits'), where('auditorRef', '==', auditorRef))
  const querySnapshot = await getDocs(userQuery)
  const items = []
  querySnapshot.forEach((docSnap) => {
    if (access.includes(userData.access)) {
      items.push({ id: docSnap.id, ...docSnap.data() })
    }
  })
  return items.sort((a, b) => {
    const aDeadline = a?.deadline?.seconds || 0
    const bDeadline = b?.deadline?.seconds || 0
    return aDeadline - bDeadline
  })
})
export const fetchAuditsForDateRange = createAsyncThunk(
  'auditsInDateRange/fetch',
  async ({ fromDate, toDate }, thunkAPI) => {
    const userData = thunkAPI.getState().user.userData
    const start = setStartDate(fromDate)
    const end = setEndDate(toDate)
    if (!start || !end) {
      throw new Error('Invalid date range')
    }
    const fromTs = Timestamp.fromDate(start)
    const toTs = Timestamp.fromDate(end)
    const rangeQuery = query(
      collection(db, 'audits'),
      where('status', '==', 1),
      where('completedDate', '>=', fromTs),
      where('completedDate', '<=', toTs),
      orderBy('completedDate', 'desc')
    )
    const querySnapshot = await getDocs(rangeQuery)
    const items = []
    querySnapshot.forEach((docSnap) => {
      if (access.includes(userData.access)) {
        items.push({ id: docSnap.id, ...docSnap.data() })
      }
    })
    return items
  }
)

const initialState = {
  fromDate: dateFormatUS(beginMonth),
  toDate: dateFormatUS(today),
  locationId: '',
  searchTerm: '',
  auditorId: '',
  personId: '',
  isContractor: 'all',
  monthlyNegativeAuditsByPerson: {
    personId: 'all',
    itemId: 'all',
    eppId: 'epp',
  },
  sort: {
    key: 'deadline',
    direction: 'caret-up',
  },
}
const auditsSlice = createSlice({
  name: 'audits',
  initialState: {
    data: [],
    status: 'idle', // 'idle' | 'loading' | 'succeeded' | 'failed'
    error: null,
    lastFetchedDateRangeKey: null,
    failedDateRangeKey: null,
    filters: {
      ...initialState,
    },
  },
  reducers: {
    auditAdded: (state, action) => {
      state.data.open.push(action.payload)
    },
    auditUpdated: (state, action) => {
      const index = state.data.findIndex((audit) => audit.id === action.payload.id)
      if (index !== -1) {
        if (action.payload.status === 2) {
          state.data.push(action.payload)
          state.data.splice(index, 1)
        } else {
          state.data[index] = action.payload
        }
      }
    },
    setFilters: (state, action) => {
      state.filters = {
        ...state.filters,
        ...action.payload,
      }
    },
    setMonthlyNegativeAuditsByPersonFilters: (state, action) => {
      state.filters.monthlyNegativeAuditsByPerson = {
        ...state.filters.monthlyNegativeAuditsByPerson,
        ...action.payload,
      }
    },
    clearFilters: (state) => {
      const t = new Date()
      const bm = new Date(t.getFullYear(), t.getMonth(), 1)
      state.filters = {
        ...initialState,
        fromDate: dateFormatUS(bm),
        toDate: dateFormatUS(t),
      }
    },
    /** Asignaciones STOP: mes completo (deadline puede ser a fin de mes). */
    clearAssignmentFilters: (state) => {
      const t = new Date()
      const bm = new Date(t.getFullYear(), t.getMonth(), 1)
      const endMonth = new Date(t.getFullYear(), t.getMonth() + 1, 0)
      state.filters = {
        ...initialState,
        fromDate: dateFormatUS(bm),
        toDate: dateFormatUS(endMonth),
      }
    },
    /** Mis auditorías STOP: mismo reset que clearFilters pero sin rango de fechas. */
    clearStopHomeFilters: (state) => {
      state.filters = {
        ...initialState,
        fromDate: '',
        toDate: '',
      }
    },
    removeFilter: (state, action) => {
      const { payload } = action
      if (payload === 'fromDate' || payload === 'toDate') {
        // preserve date filters
        // delete state.filters.fromDate
        // delete state.filters.toDate
      } else {
        delete state.filters[payload]
      }
    },
    reload: (state) => {
      state.status = 'idle'
      state.lastFetchedDateRangeKey = null
      state.failedDateRangeKey = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAudits.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchAudits.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.data = action.payload
      })
      .addCase(fetchAudits.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(fetchAuditsForUser.pending, (state) => {
        state.status = 'loading'
      })
      .addCase(fetchAuditsForUser.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.data = action.payload
      })
      .addCase(fetchAuditsForUser.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
      })
      .addCase(fetchAuditsForDateRange.pending, (state) => {
        state.status = 'loading'
        state.failedDateRangeKey = null
      })
      .addCase(fetchAuditsForDateRange.fulfilled, (state, action) => {
        state.status = 'succeeded'
        state.failedDateRangeKey = null
        const arg = action.meta.arg
        state.lastFetchedDateRangeKey =
          arg?.fromDate && arg?.toDate ? `${arg.fromDate}|${arg.toDate}` : null
        state.data = action.payload
      })
      .addCase(fetchAuditsForDateRange.rejected, (state, action) => {
        state.status = 'failed'
        state.error = action.error.message
        const arg = action.meta.arg
        state.failedDateRangeKey =
          arg?.fromDate && arg?.toDate ? `${arg.fromDate}|${arg.toDate}` : null
      })
  },
})

export const {
  auditUpdated,
  setFilters,
  setMonthlyNegativeAuditsByPersonFilters,
  clearFilters,
  clearAssignmentFilters,
  clearStopHomeFilters,
  removeFilter,
  reload,
} = auditsSlice.actions
export default auditsSlice.reducer
